import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PlusIcon } from '@/components/icons';
import { baseApi } from '@/api/base.api';
import { monitorApi } from '@/api/monitor.api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { AgentIcon, ApplicationIcon, SandboxIcon, AlertCircleIcon } from '@/components/icons';

const TIME_RANGE_OPTIONS = [
  { label: '过去1天', value: '1d' },
  { label: '过去7天', value: '7d' },
  { label: '过去30天', value: '30d' },
];

interface TimeRangeConfig {
  fromTimestamp: string;
  toTimestamp: string;
}

// 调用与 Token 卡片共用一次请求
function useAgentMetrics(timeRangeConfig: TimeRangeConfig) {
  return useQuery({
    queryKey: ['dashboard-agent-metrics', timeRangeConfig],
    queryFn: () => monitorApi.getMetrics(timeRangeConfig),
    refetchOnWindowFocus: false,
  });
}

function useSandboxMetrics(timeRangeConfig: TimeRangeConfig) {
  return useQuery({
    queryKey: ['dashboard-sandbox-metrics', timeRangeConfig],
    queryFn: () => monitorApi.getSandboxMetrics(timeRangeConfig),
    refetchOnWindowFocus: false,
  });
}

function AgentStatsCard() {
  const { data: overviewData, isLoading } = useQuery({
    queryKey: ['dashboard-overview'],
    queryFn: () => baseApi.overview(),
    refetchOnWindowFocus: false,
  });

  const navigate = useNavigate();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">资源概览</CardTitle>
        <CardDescription>平台资源汇总数据</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => navigate('/agents')}
            className="flex items-center gap-4 p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors text-left"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-500/10">
              <AgentIcon className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <div className="text-2xl font-bold">
                {isLoading ? <Skeleton className="h-8 w-16" /> : overviewData?.agentTotal || 0}
              </div>
              <div className="text-sm text-muted-foreground">智能体模版</div>
            </div>
          </button>
          <button
            onClick={() => navigate('/applications')}
            className="flex items-center gap-4 p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors text-left"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-emerald-500/10">
              <ApplicationIcon className="h-6 w-6 text-emerald-500" />
            </div>
            <div>
              <div className="text-2xl font-bold">
                {isLoading ? <Skeleton className="h-8 w-16" /> : overviewData?.appTotal || 0}
              </div>
              <div className="text-sm text-muted-foreground">智能体实例</div>
            </div>
          </button>
          <button
            onClick={() => navigate('/plugins/sandboxes')}
            className="flex items-center gap-4 p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors text-left"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-amber-500/10">
              <SandboxIcon className="h-6 w-6 text-amber-500" />
            </div>
            <div>
              <div className="text-2xl font-bold">
                {isLoading ? (
                  <Skeleton className="h-8 w-16" />
                ) : (
                  overviewData?.sandboxClassTotal || 0
                )}
              </div>
              <div className="text-sm text-muted-foreground">沙箱模版</div>
            </div>
          </button>
          <button
            onClick={() => navigate('/plugins/sandboxes')}
            className="flex items-center gap-4 p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors text-left"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-orange-500/10">
              <SandboxIcon className="h-6 w-6 text-orange-500" />
            </div>
            <div>
              <div className="text-2xl font-bold">
                {isLoading ? <Skeleton className="h-8 w-16" /> : overviewData?.sandboxTotal || 0}
              </div>
              <div className="text-sm text-muted-foreground">沙箱实例</div>
            </div>
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

function TimeRangeSelector({
  timeRange,
  onChange,
}: {
  timeRange: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex gap-1">
      {TIME_RANGE_OPTIONS.map((option) => (
        <Button
          key={option.value}
          variant={timeRange === option.value ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </Button>
      ))}
    </div>
  );
}

function CallStatsCard({ timeRangeConfig }: { timeRangeConfig: TimeRangeConfig }) {
  const { data: stats, isLoading } = useAgentMetrics(timeRangeConfig);
  const totalCalls = stats?.totalCalls ?? 0;
  const successCalls = stats?.successCalls ?? 0;
  const failedCalls = stats?.failedCalls ?? 0;
  const failureRate = totalCalls > 0 ? (failedCalls / totalCalls) * 100 : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">调用统计</CardTitle>
        <CardDescription>Agent 调用次数与成功率</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-32" />
            <Skeleton className="h-4 w-48" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-baseline gap-3">
              <span className="text-4xl font-bold">{totalCalls.toLocaleString()}</span>
              <span className="text-lg text-muted-foreground">次调用</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500/10">
                  <svg
                    className="h-4 w-4 text-emerald-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <div className="text-lg font-semibold">{successCalls.toLocaleString()}</div>
                  <div className="text-xs text-muted-foreground">成功</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-destructive/10">
                  <AlertCircleIcon className="h-4 w-4 text-destructive" />
                </div>
                <div>
                  <div className="text-lg font-semibold">{failedCalls.toLocaleString()}</div>
                  <div className="text-xs text-muted-foreground">
                    失败 ({failureRate.toFixed(1)}%)
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function formatTokenCount(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toString();
}

function TokenUsageCard({ timeRangeConfig }: { timeRangeConfig: TimeRangeConfig }) {
  const { data: stats, isLoading: tokenLoading } = useAgentMetrics(timeRangeConfig);
  const totalTokens = stats?.totalTokens ?? 0;
  const inputTokens = stats?.inputTokens ?? 0;
  const outputTokens = stats?.outputTokens ?? 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Token 用量</CardTitle>
        <CardDescription>LLM 调用 Token 消耗</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-4">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-violet-500/10">
              <svg
                className="h-4 w-4 text-violet-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                />
              </svg>
            </div>
            <div>
              <div className="text-lg font-semibold">
                {tokenLoading ? <Skeleton className="h-5 w-14" /> : formatTokenCount(totalTokens)}
              </div>
              <div className="text-xs text-muted-foreground">Total</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-500/10">
              <svg
                className="h-4 w-4 text-blue-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </div>
            <div>
              <div className="text-lg font-semibold">
                {tokenLoading ? (
                  <Skeleton className="h-5 w-14" />
                ) : (
                  formatTokenCount(inputTokens || 0)
                )}
              </div>
              <div className="text-xs text-muted-foreground">Input</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-500/10">
              <svg
                className="h-4 w-4 text-amber-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
                />
              </svg>
            </div>
            <div>
              <div className="text-lg font-semibold">
                {tokenLoading ? (
                  <Skeleton className="h-5 w-14" />
                ) : (
                  formatTokenCount(outputTokens || 0)
                )}
              </div>
              <div className="text-xs text-muted-foreground">Output</div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

function SandboxMonitorCard({ timeRangeConfig }: { timeRangeConfig: TimeRangeConfig }) {
  const { data: stats, isLoading: sandboxLoading } = useSandboxMetrics(timeRangeConfig);
  const totalRuns = stats?.sandboxRuns ?? 0;
  const totalDuration = stats?.sandboxDurationMs ?? 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">沙箱监控</CardTitle>
        <CardDescription>沙箱代码执行次数与耗时</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-500/10">
              <SandboxIcon className="h-4 w-4 text-amber-500" />
            </div>
            <div>
              <div className="text-lg font-semibold">
                {sandboxLoading ? <Skeleton className="h-5 w-14" /> : totalRuns.toLocaleString()}
              </div>
              <div className="text-xs text-muted-foreground">运行次数</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-500/10">
              <svg
                className="h-4 w-4 text-orange-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <div className="text-lg font-semibold">
                {sandboxLoading ? <Skeleton className="h-5 w-14" /> : formatDuration(totalDuration)}
              </div>
              <div className="text-xs text-muted-foreground">总运行时长</div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function QuickActionsCard() {
  const navigate = useNavigate();

  const quickActions = [
    {
      title: '创建智能体',
      description: '创建一个新的 AI 智能体',
      icon: AgentIcon,
      onClick: () => navigate('/agents/create'),
    },
    {
      title: '创建应用实例',
      description: '为智能体创建一个应用实例',
      icon: ApplicationIcon,
      onClick: () => navigate('/applications/create'),
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">快捷操作</CardTitle>
        <CardDescription>快速创建新的资源</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {quickActions.map((action) => (
          <button
            key={action.title}
            onClick={action.onClick}
            className="flex w-full items-center gap-4 p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors text-left"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <action.icon className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <div className="font-medium">{action.title}</div>
              <div className="text-sm text-muted-foreground">{action.description}</div>
            </div>
            <PlusIcon className="h-5 w-5 text-muted-foreground" />
          </button>
        ))}
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const [timeRange, setTimeRange] = useState('1d');

  const timeRangeConfig = useMemo((): TimeRangeConfig => {
    const now = Date.now();
    switch (timeRange) {
      case '1d':
        return {
          fromTimestamp: new Date(now - 24 * 60 * 60 * 1000).toISOString(),
          toTimestamp: new Date(now).toISOString(),
        };
      case '7d':
        return {
          fromTimestamp: new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString(),
          toTimestamp: new Date(now).toISOString(),
        };
      case '30d':
        return {
          fromTimestamp: new Date(now - 30 * 24 * 60 * 60 * 1000).toISOString(),
          toTimestamp: new Date(now).toISOString(),
        };
      default:
        return {
          fromTimestamp: new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString(),
          toTimestamp: new Date(now).toISOString(),
        };
    }
  }, [timeRange]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">工作台</h1>
          <p className="text-muted-foreground mt-1">欢迎使用智能体平台</p>
        </div>
        <TimeRangeSelector timeRange={timeRange} onChange={setTimeRange} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          <AgentStatsCard />
          <QuickActionsCard />
        </div>

        <div className="space-y-6">
          <CallStatsCard timeRangeConfig={timeRangeConfig} />
          <TokenUsageCard timeRangeConfig={timeRangeConfig} />
          <SandboxMonitorCard timeRangeConfig={timeRangeConfig} />
        </div>
      </div>
    </div>
  );
}
